import _ from 'lodash';
import { JitsiConnectionQualityEvents } from '../base/lib-jitsi-meet';
import { detect } from 'detect-browser';
import { updateUserDiagnostic } from 'common/helpers/api.ts';
import { trackCodecChanged } from '../base/tracks/actions.any';
import { getLocalTracks } from '../base/tracks/functions.any';
/**
 * Contains all the callbacks to be notified when stats are updated.
 *
 * {
 *     userId: Function[]
 * }.
 */
const subscribers = {};
/**
 * Timestamp of the last updateUserDiagnostic call.
 * Used to throttle the API calls to once every 10 seconds.
 */
let lastDiagnosticUpdateTime = 0;
/**
 * Minimum interval (in milliseconds) between updateUserDiagnostic calls.
 */
const DIAGNOSTIC_UPDATE_INTERVAL = 30000; // 30 seconds
/**
 * A singleton that acts as a pub/sub service for connection stat updates.
 */
const statsEmitter = {
    /**
     * Have {@code statsEmitter} subscribe to stat updates from a given
     * conference.
     *
     * @param {JitsiConference} conference - The conference for which
     * {@code statsEmitter} should subscribe for stat updates.
     * @returns {void}
     */
    startListeningForStats(conference) {
        conference.on(JitsiConnectionQualityEvents.LOCAL_STATS_UPDATED, (stats) => this._onStatsUpdated(conference.myUserId(), stats));
        conference.on(JitsiConnectionQualityEvents.REMOTE_STATS_UPDATED, (id, stats) => this._emitStatsUpdate(id, stats));
    },
    /**
     * Add a subscriber to be notified when stats are updated for a specified
     * user id.
     *
     * @param {string} id - The user id whose stats updates are of interest.
     * @param {Function} callback - The function to invoke when stats for the
     * user have been updated.
     * @returns {void}
     */
    subscribeToClientStats(id, callback) {
        if (!id) {
            return;
        }
        if (!subscribers[id]) {
            subscribers[id] = [];
        }
        subscribers[id].push(callback);
    },
    /**
     * Remove a subscriber that is listening for stats updates for a specified
     * user id.
     *
     * @param {string} id - The user id whose stats updates are no longer of
     * interest.
     * @param {Function} callback - The function that is currently subscribed to
     * stat updates for the specified user id.
     * @returns {void}
     */
    unsubscribeToClientStats(id, callback) {
        if (!subscribers[id]) {
            return;
        }
        const filteredSubscribers = subscribers[id].filter((subscriber) => subscriber !== callback);
        if (filteredSubscribers.length) {
            subscribers[id] = filteredSubscribers;
        }
        else {
            delete subscribers[id];
        }
    },
    /**
     * Emit a stat update to all those listening for a specific user's
     * connection stats.
     *
     * @param {string} id - The user id the stats are associated with.
     * @param {Object} stats - New connection stats for the user.
     * @returns {void}
     */
    _emitStatsUpdate(id, stats = {}) {
        const callbacks = subscribers[id] || [];
        callbacks.forEach((callback) => {
            callback(stats);
        });
    },
    /**
     * Emit a stat update to all those listening for local stat updates. Will
     * also update listeners of remote user stats of changes related to their
     * stats.
     *
     * @param {string} localUserId - The user id for the local user.
     * @param {Object} stats - Connection stats for the local user as provided
     * by the library.
     * @returns {void}
     */
    async _onStatsUpdated(localUserId, stats) {
        const allUserFramerates = stats.framerate || {};
        const allUserResolutions = stats.resolution || {};
        const allUserCodecs = stats.codec || {};
        // FIXME resolution and framerate are maps keyed off of user ids with
        // stat values. Receivers of stats expect resolution and framerate to
        // be primitives, not maps, so here we override the 'lib-jitsi-meet'
        // stats objects.
        const modifiedLocalStats = Object.assign({}, stats, {
            framerate: allUserFramerates[localUserId],
            resolution: allUserResolutions[localUserId],
            codec: allUserCodecs[localUserId]
        });
        const browser = detect();
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const data = {
            event: 'connection_stats',
            channel: modifiedLocalStats,
            participant_id: APP.conference.getMyUserId(),
            meeting_id: APP.conference._room.room.meetingId,
            browser
        };
        if (connection) {
            data.connection = {
                type: connection.type,
                downlink: connection.downlink,
                rtt: connection.rtt,
                saveData: connection.saveData,
                effectiveType: connection.effectiveType
            };
        }
        const diagnosticData = {
            id: data.participant_id,
            audio_bit_rate_download: data.channel.bitrate.audio.download > 0 ? data.channel.bitrate.audio.download : data.channel.bitrate.download,
            audio_bit_rate_upload: data.channel.bitrate.audio.upload > 0 ? data.channel.bitrate.audio.upload : data.channel.bitrate.upload,
            video_bit_rate_download: data.channel.bitrate.video.download > 0 ? data.channel.bitrate.video.download : data.channel.bitrate.download,
            video_bit_rate_upload: data.channel.bitrate.video.upload > 0 ? data.channel.bitrate.video.upload : data.channel.bitrate.upload,
            region: data.channel.serverRegion,
            meeting_id: data.meeting_id,
            browser_name: data.browser.name,
            browser_version: data.browser.version,
            browser_os: data.browser.os == 'Windows 10' ? 'Windows' : data.browser.os,
            token: localStorage.getItem('11S_TOKEN')
        };
        // Throttle updateUserDiagnostic to be called at most once every 10 seconds
        const currentTime = Date.now();
        if (currentTime - lastDiagnosticUpdateTime >= DIAGNOSTIC_UPDATE_INTERVAL) {
            lastDiagnosticUpdateTime = currentTime;
            // Wrap in try-catch to prevent unhandled promise rejections
            try {
                await updateUserDiagnostic(diagnosticData);
            }
            catch (err) {
                // Error is already handled inside updateUserDiagnostic, but catch here too
                // to prevent any potential unhandled promise rejection
                if (process.env.NODE_ENV === 'development') {
                    console.warn('[STATS EMITTER] updateUserDiagnostic error (already handled):', err);
                }
            }
        }
        modifiedLocalStats.codec
            && Object.keys(modifiedLocalStats.codec).length
            && this._updateLocalCodecs(modifiedLocalStats.codec);
        this._emitStatsUpdate(localUserId, modifiedLocalStats);
        // Get all the unique user ids from the framerate and resolution stats
        // and update remote user stats as needed.
        const framerateUserIds = Object.keys(allUserFramerates);
        const resolutionUserIds = Object.keys(allUserResolutions);
        const codecUserIds = Object.keys(allUserCodecs);
        _.union(framerateUserIds, resolutionUserIds, codecUserIds)
            .filter(id => id !== localUserId)
            .forEach(id => {
            const remoteUserStats = {};
            const framerate = allUserFramerates[id];
            if (framerate) {
                remoteUserStats.framerate = framerate;
            }
            const resolution = allUserResolutions[id];
            if (resolution) {
                remoteUserStats.resolution = resolution;
            }
            const codec = allUserCodecs[id];
            if (codec) {
                remoteUserStats.codec = codec;
            }
            this._emitStatsUpdate(id, remoteUserStats);
        });
    },
    /**
     * Updates the codec associated with the local tracks.
     * This is currently used for torture tests.
     *
     * @param {any} codecs - Codec information per local SSRC.
     * @returns {void}
     */
    _updateLocalCodecs(codecs) {
        if (typeof APP !== 'undefined') {
            const tracks = APP.store.getState()['features/base/tracks'];
            const localTracks = getLocalTracks(tracks);
            for (const track of localTracks) {
                const ssrc = track.jitsiTrack?.getSsrc();
                if (ssrc && Object.keys(codecs).find(key => Number(key) === ssrc)) {
                    const codecsPerSsrc = codecs[ssrc];
                    const codec = codecsPerSsrc.audio ?? codecsPerSsrc.video;
                    if (track.codec !== codec) {
                        APP.store.dispatch(trackCodecChanged(track.jitsiTrack, codec));
                    }
                }
            }
        }
    }
};
export default statsEmitter;
